/**
 * Maestro UI settings that come with a session opened from a short URL.
 *
 * Host-chosen Maestro setup is carried on the session (`session.maestro`, set
 * in the Dashboard and delivered in the ?session= payload):
 *
 *   { previewRendererUrl, locked: {...flags}, initial: {...flags},
 *     flowPreview?: { source: 'aero' | 'whep', endpoint, rendererId, apiKey } }
 *
 * The Maestro library reads its UI settings from the `maestro-ui-settings`
 * localStorage key when it mounts, and persisted values beat the
 * defaultUiSettings seed — so applying a session's setup means writing its
 * values into that key. The operator's own settings are set aside the first
 * time a session setup is applied and restored when a session without one is
 * opened, so a host's setup never permanently replaces them.
 *
 * `programRendererUrl` (where IN/OUT/BAKE dispatch goes) always follows the
 * renderer: the session's own renderer at launch, then whichever renderer the
 * operator switches to in Drive (see writeProgramRendererUrl and
 * ProgramRendererUrlSync).
 *
 * The preview itself (Aero URL, or a Flow WHEP stream and its API key) is read
 * from the Flow preview settings (see services/flowPreview.service.js), so the
 * session's previewRendererUrl and flowPreview are written there too, with the
 * operator's own preview settings set aside and restored the same way.
 */

const UI_SETTINGS_KEY = 'maestro-ui-settings';
const OPERATOR_BACKUP_KEY = 'aedrive_maestro_operator_ui_settings';
const OPERATOR_PREVIEW_BACKUP_KEY = 'aedrive_maestro_operator_preview_settings';
// Where the Flow preview settings (services/flowPreview.service.js, OPUS-1763)
// read the preview source and API key from.
const FLOW_SETTINGS_KEY = 'aedrive.flowPreview.settings';
const FLOW_API_KEY = 'aedrive.flowPreview.apiKey';

const readSettings = () => {
  try {
    const raw = localStorage.getItem(UI_SETTINGS_KEY);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

/**
 * Aero URL of a renderer, matching how TXLive builds it. '' when there is no
 * renderer (or no Aero base configured), so Maestro falls back to its
 * rendererId prop.
 * @param {string|null|undefined} rendererId
 * @returns {string}
 */
export const rendererUrlFor = (rendererId) => {
  const base = (process.env.REACT_APP_RENDERER_PREVIEW_URL || '').replace(/\/$/, '');
  return rendererId && base ? `${base}/?renderer=${encodeURIComponent(rendererId)}` : '';
};

const restoreRaw = (key, raw) => {
  if (raw === null || raw === undefined) localStorage.removeItem(key);
  else localStorage.setItem(key, raw);
};

/**
 * The session's changes to the Flow preview settings, or null when it leaves
 * the preview to the operator. A Flow WHEP preview also brings its API key,
 * kept (`remember`) so the preview can connect.
 * @param {Object|null|undefined} maestro
 * @returns {Object|null}
 */
const sessionPreviewOverrides = (maestro) => {
  const { previewRendererUrl, flowPreview } = maestro || {};
  const whep = flowPreview?.source === 'whep';
  const overrides = {
    ...(previewRendererUrl ? { aeroUrl: previewRendererUrl } : {}),
    ...(flowPreview ? { source: whep ? 'whep' : 'aero' } : {}),
    ...(whep ? { endpoint: flowPreview.endpoint || '', rendererId: flowPreview.rendererId || '', remember: true } : {}),
  };
  return Object.keys(overrides).length ? overrides : null;
};

/**
 * Apply a session's preview source to the Flow preview settings, on top of
 * the operator's own (set aside the first time), or put theirs back when the
 * session has none.
 * @param {Object|null|undefined} maestro
 */
const applySessionPreview = (maestro) => {
  const overrides = sessionPreviewOverrides(maestro);
  const backupRaw = localStorage.getItem(OPERATOR_PREVIEW_BACKUP_KEY);

  if (!overrides) {
    if (backupRaw !== null) {
      const backup = JSON.parse(backupRaw);
      restoreRaw(FLOW_SETTINGS_KEY, backup.settings);
      restoreRaw(FLOW_API_KEY, backup.apiKey);
      localStorage.removeItem(OPERATOR_PREVIEW_BACKUP_KEY);
    }
    return;
  }

  const backup = backupRaw !== null
    ? JSON.parse(backupRaw)
    : { settings: localStorage.getItem(FLOW_SETTINGS_KEY), apiKey: localStorage.getItem(FLOW_API_KEY) };
  if (backupRaw === null) localStorage.setItem(OPERATOR_PREVIEW_BACKUP_KEY, JSON.stringify(backup));

  // Build on the operator's own settings, not an earlier session's.
  const own = backup.settings ? JSON.parse(backup.settings) || {} : {};
  localStorage.setItem(FLOW_SETTINGS_KEY, JSON.stringify({ ...own, ...overrides }));
  if (overrides.source === 'whep') localStorage.setItem(FLOW_API_KEY, maestro.flowPreview.apiKey || '');
  else restoreRaw(FLOW_API_KEY, backup.apiKey);
};

/**
 * Apply a launched session's Maestro settings to `maestro-ui-settings`.
 * Safe to call repeatedly for the same session.
 * @param {Object|null|undefined} session  the ?session= payload ({ maestro, rendererId, ... })
 */
export const applySessionMaestroSettings = (session) => {
  try {
    const maestro = session?.maestro;
    let settings;

    if (maestro) {
      if (localStorage.getItem(OPERATOR_BACKUP_KEY) === null) {
        localStorage.setItem(OPERATOR_BACKUP_KEY, JSON.stringify({ raw: localStorage.getItem(UI_SETTINGS_KEY) }));
      }
      settings = {
        ...readSettings(),
        ...(maestro.locked || {}),
        ...(maestro.initial || {}),
        ...(maestro.previewRendererUrl ? { previewRendererUrl: maestro.previewRendererUrl } : {}),
      };
    } else {
      // Session leaves Maestro to the operator — put their own settings back.
      const backup = localStorage.getItem(OPERATOR_BACKUP_KEY);
      if (backup !== null) {
        const { raw } = JSON.parse(backup);
        if (raw === null) localStorage.removeItem(UI_SETTINGS_KEY);
        else localStorage.setItem(UI_SETTINGS_KEY, raw);
        localStorage.removeItem(OPERATOR_BACKUP_KEY);
      }
      settings = readSettings();
    }

    localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify({
      ...settings,
      programRendererUrl: rendererUrlFor(session?.rendererId),
    }));
    applySessionPreview(maestro);
  } catch {
    // Storage unavailable — Maestro falls back to its defaults.
  }
};

/**
 * Point `programRendererUrl` at a renderer, for the next Maestro mount.
 * @param {string|null|undefined} rendererId
 */
export const writeProgramRendererUrl = (rendererId) => {
  try {
    localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify({
      ...readSettings(),
      programRendererUrl: rendererUrlFor(rendererId),
    }));
  } catch {
    // Storage unavailable — Maestro falls back to its rendererId prop.
  }
};
